'use client';

import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type {
  PaylinkAmountType,
  PaylinkAmountWithCurrency,
  PaylinkCauseType,
} from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Card, CardHeader, CardContent } from '@packages/shared/src/ui/Card/Card';
import { AccountInformation } from '../AccountInformation/AccountInformation';
import { EnterPaymentAmount } from './EnterPaymentAmount';

const messages = defineMessages({
  downPaymentBankInfo: {
    id: 'PaylinkContent.downPaymentBankinfo',
    defaultMessage: 'Sparkasse Coburg/Lichtenfels',
  },
  downPaymentIban: {
    id: 'PaylinkContent.downPaymentIban',
    defaultMessage: 'IBAN: DE35 7835 0000 0040 9076 36',
  },
  downPaymentBic: {
    id: 'PaylinkContent.downPaymentBic',
    defaultMessage: 'BIC: BYLADEM1COB',
  },
  subHeadline: {
    id: 'PaylinkContent.subheadline',
    defaultMessage:
      'Jetzt schnell, einfach & direkt mit folgenden Optionen {paymentType, select, DOWNPAYMENT { Ihre offene Anzahlung } other { Ihren offenen Betrag }} begleichen.',
  },
});

export type PaylinkContentProps = {
  type: PaylinkCauseType | null;
  amount: number | null;
  amountWithCurrency: PaylinkAmountWithCurrency | null;
  amountType: PaylinkAmountType | null;
  title: string | null;
  referenceCode: string | null;
};

export const PaylinkContent: FC<{
  data: PaylinkContentProps;
  onAmountChange: (newAmount: number) => void;
}> = ({ data, onAmountChange }) => {
  const { formatMessage } = useIntl();
  const {
    order: {
      paylink: { respectTypeFromApi },
    },
  } = useConfig();
  const { title, type, referenceCode, amountWithCurrency, amountType } = data;
  const showDownpaymentFormatting = respectTypeFromApi ? type === 'DOWNPAYMENT' : true;
  return (
    <>
      <h1 className="text-size-headline-2xl mb-1 pl-2.5">
        <FormattedMessage
          key="PaylinkContent.headline-text"
          id="PaylinkContent.headline"
          defaultMessage="Sofort offenen Betrag begleichen"
        />
      </h1>
      <div className="text-size-body-sm mb-1 pl-2.5">
        {formatMessage(messages.subHeadline, {
          paymentType: showDownpaymentFormatting ? 'DOWNPAYMENT' : type,
        })}
      </div>
      <div className="bg-color-grey-light box-border flex flex-col gap-2 p-2.5 md:flex-row">
        {/* Ausgleichszahlung Card */}
        <div className="flex-1">
          <Card>
            <CardHeader hasDivider>{title}</CardHeader>
            <CardContent className="pt-1.5 pb-2">
              <EnterPaymentAmount
                amount={amountWithCurrency}
                amountType={amountType}
                onChange={(value) => onAmountChange(value)}
              />
            </CardContent>
          </Card>
        </div>
        {/* Bankverbindung Card */}
        <div className="flex-1">
          <Card>
            <CardContent className="pt-1.5 pb-2">
              {showDownpaymentFormatting ? (
                <AccountInformation
                  customIbanMessage={formatMessage(messages.downPaymentIban)}
                  customBicMessage={formatMessage(messages.downPaymentBic)}
                  customReceiverMessage={formatMessage(messages.downPaymentBankInfo)}
                  referenceCode={referenceCode}
                />
              ) : (
                <AccountInformation referenceCode={referenceCode} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
